enum BookingStatus {
    HOLD = 'HOLD',
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    PAYMENT_PENDING = 'PAYMENT_PENDING',
}

enum BookingType {
    SINGLE_SLOT = 'SINGLE_SLOT',
    MULTI_DAY = 'MULTI_DAY',
}

enum ToDoStatus {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    REOPENED = 'REOPENED',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED',
}

type ToDo = {
    id?: number;
    title: string;
    description?: string;
    due_date: Date;
    priority: Priority;
    status: ToDoStatus;
    created_on: Date;
    updated_on?: Date;
    created_user_id: number;
    assigned_user_id?: number;
    completed_on?: Date;
}

type Booking = {
    id?: number;
    created_on: string;
    updated_on?: string;
    amenity: {
        id: number;
        title: string;
        description: string;
        slot_duration: string;
        require_approval?: boolean;
        require_payment?: boolean;
        price?: number;
    };
    requesting_user: {
        id: number;
        name: string;
        email?: string;
        phone?: string;
        avatar?: string;
    };
    requester_unit: {
        id: number;
        unit_number: string;
    }
    slots?: Date[];
    booking_start: Date;
    booking_end: Date;
    type: BookingType;
    status: BookingStatus;
    comments?: string;
};

type HoldBooking = {
    amenity_id: number;
    requesting_user_id: number;
    requester_unit_id: number;
    slots?: Date[];
    booking_start: Date;
    booking_end?: Date;
    type: BookingType;
    status: BookingStatus;
};

enum APICallStatus {
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE'
}

enum DataTypes {
    SERVICE_REQUEST = 'SERVICE_REQUEST',
    VIOLATION = 'VIOLATION',
    VISITOR_PARKING = 'VISITOR_PARKING',
    AMENITY = 'AMENITY',
    ANNOUNCEMENT = 'ANNOUNCEMENT',
    BOOKING = 'BOOKING',
    UNIT = 'UNIT',
    USER = 'USER',
    TODO = 'TODO',
}

enum ServiceRequestStatus {
    NEW = 'NEW',
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    CANCELLED = 'CANCELLED'
}

enum ViolationStatus {
    OPEN = 'OPEN',
    CLOSED = 'CLOSED',
    OWNER_ACTION_REQUIRED = 'OWNER_ACTION_REQUIRED',
    RESOLVED = 'RESOLVED',
    DRAFT = 'DRAFT',
}

enum UnitType {
    STUDIO = 'STUDIO',
    ONE_BEDROOM = 'ONE_BEDROOM',
    TWO_BEDROOM = 'TWO_BEDROOM',
    THREE_BEDROOM = 'THREE_BEDROOM',
    PENTHOUSE = 'PENTHOUSE',
}

type Unit = {
    id: number;
    unit_number: number;
    building_id: number;
    floor_number: number;
    unit_type: UnitType;
    is_occupied: boolean;
    owner?: {id: number, name: string};
    tenants?: {id: number, name: string}[];
    created_on: Date;
    updated_on?: Date;
    parking_slot?: string;
    access_fob?: string[];
    buzzer_code?: string;
    locker_number?: string;
}

enum UserType {
    MANAGER = 'MANAGER',
    BOARD_MEMBER = 'BOARD_MEMBER',
    OWNER = 'OWNER',
    TENANT = 'TENANT',
    SECURITY = 'SECURITY',
}

type User = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    type: UserType;
    is_active: boolean;
    avatar: string;
    emergency_contact?: {
        name: string;
        phone: string;
        email: string;
        relationship: string;
    };
};

export interface AuthContextType {
    user: User;
    isAuthenticated: boolean;
    isLoading: boolean;
    token: string | null;
    login: (userData: FormData) => Promise<boolean>;
    logout: () => void;
}

export interface AppContextType {
    loading: boolean;
    setLoading: (loading: boolean) => void;
    snackbar: {
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'warning' | 'info';
        timer?: number;
    };
    warningDialog: {
        open: boolean;
        message: string;
        title: string;
        onConfirm: () => void;
        onCancel: () => void;
    };
    setWarningDialog: (open: boolean, message: string, title: string, onConfirm: () => void, onCancel: () => void) => void;
    setSnackbar: (open: boolean, message: string, severity: 'success' | 'error' | 'warning' | 'info', timer?: number ) => void;
    setDialog: (title: string, message: string, onConfirm: () => void, onCancel?: () => void) => void;
}

enum Priority {
    LOWEST = "LOWEST",
    LOW = "LOW",
    MEDIUM = "MEDIUM",
    HIGH = "HIGH",
    CRITICAL = "CRITICAL"
}

enum ServiceRequestTypes {
    GENERAL = "GENERAL",
    MAINTENANCE = "MAINTENANCE",
    FOB_REQUEST = "FOB_REQUEST",
    PARKING_ISSUE = "PARKING_ISSUE",
    TENANT_RELATED = "TENANT_RELATED",
    COMPLAINTS = "COMPLAINTS",
    FEEDBACK = "FEEDBACK",
    EMERGENCY = "EMERGENCY",
    OTHER = "OTHER"
}

type ServiceRequestUpdate = {
    id?: number,
    service_request_id: string,
    comment: string,
    is_deleted?: boolean,
    commenter_id: string,
    created_on?: Date,
    updated_on?: Date,
    user_first_name?: string,
    user_last_name?: string,
    user_avatar?: string,
}

type Comment = {
    id?: number,
    resource_id: number,
    comment: string,
    is_deleted?: boolean,
    commenter_id: number,
    created_on?: Date,
    updated_on?: Date,
    user: {
        first_name: string,
        last_name: string,
        avatar: string,
        id: number
    }
}

type CalendarEvent = {
  id?: number;
  title: string;
  description: string;
  start_time: Date;
  end_time: Date;
  location?: string;
  image?: string;
  capacity: number;
  all_day?: boolean;
  editable?: boolean;
  creater?: {
    id: number;
    name: string;
  };
  changeEvent?: (event: CalendarEvent) => void;
}

type ServiceRequest = {
    id: number;
    title: string;
    description: string;
    priority: Priority;
    type: ServiceRequestTypes,
    reporter: User;
    reporterUnit: string;
    createdOn: string;
    updatedOn?: string;
    status: ServiceRequestStatus;
    estimatedResolutionDate: string;
};

type CreateServiceRequestData = {
    title: string;
    description: string;
    serviceType: ServiceRequestTypes;
    status: ServiceRequestStatus;
    priority: Priority;
    reporter_id: number;
    reporter_unit_id: number;
    estimatedResolutionDate?: Date;
    has_permission_to_enter: boolean;
};

type UserCredentials = {
    email: string;
    password: string;
  }

type ViewServiceRequestData = {
    id?: string;
    title: string;
    description: string;
    type: ServiceRequestTypes;
    status: ServiceRequestStatus;
    priority: Priority;
    reporter_id: number;
    reporter_unit_id: number;
    estimated_resolution_date?: string | null;
    user_first_name: string;
    user_last_name: string;
    unit_number: string;
    created_on: Date;
    has_permission_to_enter: boolean;
}

type UpdateServiceRequestData = {
    status?: ServiceRequestStatus,
    type?: ServiceRequestTypes,
    otherType?: string,
    description?: string,
    title?: string,
    priority?: Priority,
    estimatedResolutionDate?: Date,
    images?: string[],
}

type Amenity = {
    id?: number,
    title: string,
    description: string,
    slot_duration: string,
    start_time: string,
    end_time: string,
    require_payment: boolean,
    require_manager_approval: boolean,
    price?: number,
    max_slot_booking?: number,
    allow_weekends: boolean,
    picture: string,
    created_on: Date,
    updated_on: Date,
    comments?: string,
}

enum ViolationStage {
    FIRST_VIOLATION = 'FIRST_VIOLATION',
    VERBAL_WARNING = 'VERBAL_WARNING',
    WRITTEN_WARNING = 'WRITTEN_WARNING',
    FINE = 'FINE',
    LEGAL_ACTION = 'LEGAL_ACTION',
}

enum ViolationType {
    GENERAL = 'GENERAL',
    NOISE = 'NOISE',
    PARKING = 'PARKING',
    TRASH = 'TRASH',
    PET = 'PET',
    OTHER = 'OTHER'
}

type Violation = {
    id: number;
    title: string;
    description: string;
    possible_penalties: string;
    created_on: string;
    updated_on?: string;
    violation_date: Date;
    due_date?: Date;
    unit: {
        id: number;
        unit_number: string;
    };
    type: ViolationType;
    other_type?: string;
    stage: ViolationStage;
    status: ViolationStatus;
    resolution_date?: Date;
};

enum VisitorParkingRequestStatus {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED'
}

type VisitorParkingRequest = {
    id?: number;
    car: {
        make: string;
        model: string;
        plate: string;
        color: string;
    }
    date_requested: {
        start: string;
        end: string;
    }
    visitorFor?: User;
    createdOn?: string;
    updatedOn?: string;
    visitor_unit: string;
    external_person_email?: string;
    status: VisitorParkingRequestStatus;
    approvedDate?: string;
    approvedBy?: string;
};

enum SendAnnouncementOptions {
    SELECT_OPTION = '',
    ALL = 'ALL',
    BOARD = 'BOARD',
    UNIT = 'UNIT',
    UNIT_OWNERS = 'UNIT_OWNERS',
    INDIVIDUAL = 'INDIVIDUAL',
}

enum AnnouncementsPriority {
    HIGH = 'HIGH',
    MEDIUM = 'MEDIUM',
    LOW = 'LOW',
    INFORMATIONAL = 'INFORMATIONAL'
}

enum AnnouncementStatus {
    DRAFT = 'DRAFT',
    SENT = 'SENT',
    PARTIALLY_SENT = 'PARTIALLY_SENT',
    FAILED_TO_SEND = 'FAILED_TO_SEND'
}

type Announcements = {
    id: number;
    title: string;
    description: string;
    status: AnnouncementStatus;
    seen_by: number;
    created_on: string;
    updated_on?: string;
    priority: AnnouncementsPriority;
};

export type {
    UserCredentials,
    Booking,
    HoldBooking,
    Unit,
    ServiceRequest,
    Amenity,
    Violation,
    VisitorParkingRequest,
    ViewServiceRequestData,
    CreateServiceRequestData,
    UpdateServiceRequestData,
    ServiceRequestUpdate,
    Announcements,
    Comment,
    CalendarEvent,
    User,
    ToDo
}

export {
    UnitType,
    DataTypes,
    BookingType,
    UserType,
    APICallStatus,
    ToDoStatus,
    AnnouncementsPriority,
    AnnouncementStatus,
    SendAnnouncementOptions,
    ServiceRequestStatus,
    ServiceRequestTypes,
    ViolationType,
    ViolationStage,
    ViolationStatus,
    BookingStatus,
    VisitorParkingRequestStatus,
    Priority
}